// @flow
import './Rules.scss';
import type { RuleType } from 'types/rule-types';

import _ from 'lodash';
import classnames from 'classnames';

import React, { Component } from 'react';
import { Popup, Icon } from 'semantic-grab-ui/src';
import { Menu, Dropdown, Button } from 'grab-ui';
import LeafRule from './rules/LeafRule';
import Toastr from 'utils/toastr-util';
import {
  isLeafRule,
  getRuleType,
  humanize,
} from './utils';

type Props = {
  currentlySelected: boolean,
  currentlyHovered: boolean,
  onRuleRemove: Function,
  onRuleChange: Function,
  onRuleHover: Function,
  onRuleUnHover: Function,
  onRuleSelection: Function,
  onOperatorSelection: Function,
  ruleTypes: Array<RuleType>,
  root: boolean,
  rule: Object,
  readonly: boolean,
  config: Object,
  systemType: string,
};

const CUSTOM_RENDER_RULE_TYPES = ['dayOfWeek', 'timeOfDay'];

export default class Rule extends Component {
  props: Props;

  constructor(props: Props) {
    super(props);

    this.state = {
      error: '',
    };

    this.ruleComponentMap = props.config.customRenderers || {};
    this.hiddenRuleTypes = props.config.hiddenRuleTypes || [];
    this.geofenceVariable = props.config.geofenceVariable || 'missing_geofence_variable';
  }

  handleOperatorSelection = (data: Object) => {
    if (this.props.rule.conditions.length > 24) {
      Toastr.error('Conditions on a level cannot be more than 25');
    } else {
      this.props.onOperatorSelection(this.props.rule.uid, data.item.props.value);
    }
  }

  handleRuleChange = (updatedRule: Object, error: string) => {
    this.props.onRuleChange(updatedRule, error);
  }

  handleRuleHover = () => {
    this.props.onRuleHover(this.props.rule.uid);
  }

  handleRuleUnHover = () => {
    this.props.onRuleUnHover();
  }

  handleRuleSelection = () => {
    this.props.onRuleSelection(this.props.rule.uid);
  }

  handleRuleRemove = () => {
    this.props.onRuleRemove(this.props.rule.uid);
  }

  render() {
    const { rule, ruleTypes } = this.props;

    if (_.isEmpty(ruleTypes)) return null;

    if (isLeafRule(rule)) {
      return this.renderLeafRule();
    }
    return this.renderNestedRule();
  }

  renderLeafRule = () => {
    const { config, rule, ruleTypes, readonly, cityIDs = [], rules = {} } = this.props;
    const isSelected = this.props.currentlySelected === rule.uid;
    const isHovered = isSelected || this.props.currentlyHovered === rule.uid;

    const ruleType = getRuleType(ruleTypes, rule.lhs) || {};
    // RuleComponentWraper structure options:
    // - GeoRule
    // - [GeoRule, extraProps]
    //
    // extraProps can be:
    // - Object, will be merged directly
    // - Function(state), will be used as selector in the rule component
    let RuleComponentWraper;
    if (rule.lhs.indexOf(this.geofenceVariable) === 0) {
      RuleComponentWraper = _.get(
        this.ruleComponentMap,
        this.geofenceVariable,
        LeafRule
      );
    } else if (
      this.props.systemType === 'newtrident' &&
      CUSTOM_RENDER_RULE_TYPES.includes(ruleType.displayType)
    ) {
      RuleComponentWraper = _.get(
        this.ruleComponentMap,
        ruleType.displayType,
        LeafRule
      );
    } else {
      RuleComponentWraper = _.get(this.ruleComponentMap, rule.lhs, LeafRule);
    }

    let RuleComponent;
    let ruleComponentProps = {};
    if (_.isArray(RuleComponentWraper)) {
      RuleComponent = RuleComponentWraper[0];
      const extraProps = RuleComponentWraper[1];
      if (_.isFunction(extraProps)) {
        ruleComponentProps = {
          customSelector: extraProps,
        };
      } else {
        ruleComponentProps = extraProps;
      }
    } else {
      RuleComponent = RuleComponentWraper;
    }

    const props = {
      rule,
      ruleType,
      isSelected,
      readonly,
      cityIDs,
      rules,
      onRuleChange: this.handleRuleChange,
      onRuleRemove: this.handleRuleRemove,
      ...ruleComponentProps,
    };
    return (
      <div
        className={classnames('rule', {
          selected: isSelected,
          hovered: isHovered,
          'has-error': this.state.error,
        })}
        onMouseEnter={this.handleRuleHover}
        onMouseLeave={this.handleRuleUnHover}
        onClick={this.handleRuleSelection}
      >
        <span className="remove-rule" onClick={this.handleRuleRemove}>×</span>
        <RuleComponent {...props} />
        {this.state.error &&
          <span>
            <span><Icon color="red" name="warning circle" /></span>
            <span className="rule-validation-tooltip">
              {`${this.state.error}. ${_.get(config.customError, rule.lhs, '')}`}
            </span>
          </span>
        }
      </div>
    );
  }

  renderRuleMenu = () => {
    let ruleTypes = this.props.ruleTypes;
    if (this.props.systemType !== 'newtrident') {
      ruleTypes = this.props.ruleTypes.filter(rt => this.hiddenRuleTypes.indexOf(rt.variableName) === -1);
    }

    if (this.props.readonly || (this.props.rule.operator === 'and' && !this.props.root)) return null;

    const rootRuleTypes = ruleTypes.filter(rt => rt.group === 'ROOT');
    const ruleGroups = _.groupBy(ruleTypes.filter(rt => rt.group !== 'ROOT'), 'group');
    const renderRuleItem = ruleType => (
      <Menu.Item
        key={ruleType.variableName}
        value={ruleType.variableName}
      >
        {ruleType.noun}
        &nbsp;
        {!_.isEmpty(ruleType.description) && <Popup
          trigger={<Icon name="info circle" />}
          content={ruleType.description}
          size="mini"
        />}
      </Menu.Item>
    );
    const { SubMenu } = Menu;

    const menu =
    (<Menu onClick={this.handleOperatorSelection}>
      {_.map(ruleGroups, (val, key) => (
        <SubMenu title={humanize(key)} key={key}>
          {_.map(val, renderRuleItem)}
        </SubMenu>
      ))}
      {_.map(rootRuleTypes, renderRuleItem)}
    </Menu>);
    const isRoot = this.props.root;
    return (
      <Dropdown overlay={menu}>
        <Button>{isRoot ? 'AND' : 'OR'}</Button>
      </Dropdown>
    );
  }

  renderNestedRule = () => {
    const { rule } = this.props;

    return (
      <div className="rule-set">
        {(rule.conditions || []).map((subRule, index) => {
          return (
            <span key={subRule.uid}>
              <Rule
                {...this.props}
                rule={subRule}
                root={false}
              />
              { index + 1 !== rule.conditions.length && <span className="set-operator">{rule.operator}</span> }
            </span>
          );
        })}
        {this.renderRuleMenu()}
      </div>
    );
  }
}
